import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';

const API_BASE = import.meta.env.VITE_API_URL ?? 'https://api.memegenes.com';

/**
 * Price display preference, SOL/USD, mirroring the MemeGenes switcher. Defaults
 * to USD (most consumers think in dollars), persists to localStorage, and
 * drives every price on the site via `useCurrency().format`.
 */
export type Currency = 'USD' | 'SOL';

// Fallback SOL→USD rate if the live price feed can't be reached (fail-open).
const FALLBACK_RATE = 140;

const STORAGE_KEY = 'provny.currency';

const usd = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 2,
});

/**
 * Format a SOL-denominated price in the chosen currency. Prefer the bound
 * `format` from `useCurrency()` so prices use the live rate and re-render when
 * it arrives; this raw function exists for the few places without context.
 */
export function formatPrice(sol: number, currency: Currency, rate: number = FALLBACK_RATE): string {
  return currency === 'USD' ? usd.format(sol * rate) : `${sol} SOL`;
}

type CurrencyContextValue = {
  currency: Currency;
  setCurrency: (c: Currency) => void;
  /** Format a SOL price in the current currency at the live rate. */
  format: (sol: number) => string;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrency] = useState<Currency>(
    () => (localStorage.getItem(STORAGE_KEY) as Currency) || 'USD',
  );
  const [rate, setRate] = useState(FALLBACK_RATE);

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, currency);
  }, [currency]);

  // Fetch the live SOL→USD rate once on mount. Fail-open: keep the fallback on
  // any error so prices always render.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/api/payment/price`);
        if (!res.ok) return;
        const data = (await res.json()) as { sol_usdt?: number };
        if (active && typeof data.sol_usdt === 'number' && data.sol_usdt > 0) {
          setRate(data.sol_usdt);
        }
      } catch {
        // keep fallback
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  const format = (sol: number) => formatPrice(sol, currency, rate);

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, format }}>
      {children}
    </CurrencyContext.Provider>
  );
}

export function useCurrency(): CurrencyContextValue {
  const ctx = useContext(CurrencyContext);
  if (!ctx) throw new Error('useCurrency must be used within CurrencyProvider');
  return ctx;
}
